import React, { useEffect, useState } from "react";
import { PageHeader, Modal, Toast, EmptyState } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { lock } from "@/lib/perm";

import RowActions from "@/components/RowActions";
export default function DocumentCategories() {
  const { has } = useAuth();
  const [categories, setCategories] = useState(null);
  const [typesByCategory, setTypesByCategory] = useState({});
  const [editingCat, setEditingCat] = useState(null);
  const [editingType, setEditingType] = useState(null); // { category_id, id?, name, order_index, active }
  const [msg, setMsg] = useState({ text: "", type: "info" });

  const load = async () => {
    const c = await api.get("/employee-files/categories", { params: { active_only: false } });
    const sorted = [...c.data].sort((a, b) => a.order_index - b.order_index);
    setCategories(sorted);
    const entries = await Promise.all(sorted.map(async cat => {
      const t = await api.get(`/employee-files/categories/${cat.id}/types`, { params: { active_only: false } });
      return [cat.id, [...t.data].sort((a, b) => a.order_index - b.order_index)];
    }));
    setTypesByCategory(Object.fromEntries(entries));
  };
  useEffect(() => { load(); }, []);

  if (!categories) return <div style={{ padding: 40 }}>Se încarcă...</div>;

  const saveCat = async () => {
    if (!editingCat.name?.trim()) return setMsg({ text: "Denumirea este obligatorie", type: "error" });
    try {
      if (editingCat.id) await api.put(`/employee-files/categories/${editingCat.id}`, editingCat);
      else await api.post("/employee-files/categories", editingCat);
      setEditingCat(null); load();
      setMsg({ text: "Salvat", type: "success" }); setTimeout(() => setMsg({ text: "" }), 1500);
    } catch (e) { setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" }); }
  };

  const toggleCat = async (cat) => {
    try {
      await api.put(`/employee-files/categories/${cat.id}`, { ...cat, active: !cat.active });
      load();
    } catch (e) { setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" }); }
  };

  const saveType = async () => {
    if (!editingType.name?.trim()) return setMsg({ text: "Denumirea este obligatorie", type: "error" });
    try {
      if (editingType.id) await api.put(`/employee-files/types/${editingType.id}`, editingType);
      else await api.post(`/employee-files/categories/${editingType.category_id}/types`, editingType);
      setEditingType(null); load();
      setMsg({ text: "Salvat", type: "success" }); setTimeout(() => setMsg({ text: "" }), 1500);
    } catch (e) { setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" }); }
  };

  const toggleType = async (t) => {
    try {
      await api.put(`/employee-files/types/${t.id}`, { ...t, active: !t.active });
      load();
    } catch (e) { setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" }); }
  };

  return (
    <>
      <PageHeader title="Categorii Documente" subtitle="Structura dosarului electronic al angajatului"
        actions={<button className="btn btn-primary" onClick={() => setEditingCat({ name: "", order_index: categories.length })} data-testid="add-category" {...lock(has("documents.categories_manage"))}>+ Categorie nouă</button>} />
      <Toast msg={msg.text} type={msg.type} />

      <div style={{ display: "grid", gap: 14 }}>
        {categories.map(cat => (
          <div key={cat.id} className="card fade-up" style={{ padding: 20, opacity: cat.active ? 1 : 0.55 }} data-testid={`category-${cat.name}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>
                {cat.name} {!cat.active && <span className="badge badge-inactive" style={{ marginLeft: 8 }}>Inactivă</span>}
              </div>
              <RowActions testid={`row-menu-${cat.name}`} items={[
                { label: "Editează", icon: "edit", onClick: () => setEditingCat({ ...cat }), ...lock(has("documents.categories_manage")) },
                { label: cat.active ? "Dezactivează" : "Reactivează", icon: cat.active ? "close" : "check", danger: cat.active, btnDanger: cat.active, primary: !cat.active, onClick: () => toggleCat(cat), ...lock(has("documents.categories_manage")) },
              ]} />
            </div>

            <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
              {(typesByCategory[cat.id] || []).map(t => (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: "#F8FAFC", borderRadius: 6, opacity: t.active ? 1 : 0.55 }} data-testid={`type-${t.name}`}>
                  <span style={{ flex: 1, fontSize: 13 }}>{t.name} {!t.active && <span style={{ color: "#94A3B8" }}>(inactiv)</span>}</span>
                  <button className="btn btn-secondary" style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => setEditingType({ ...t })} {...lock(has("documents.categories_manage"))}>Editează</button>
                  <button className={`btn ${t.active ? "btn-danger" : "btn-primary"}`} style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => toggleType(t)} {...lock(has("documents.categories_manage"))}>
                    {t.active ? "Dezactivează" : "Reactivează"}
                  </button>
                </div>
              ))}
              {(typesByCategory[cat.id] || []).length === 0 && <EmptyState text='Niciun tip de document. Apasă „+ Tip document" mai jos.' />}
              <button className="btn btn-secondary" style={{ alignSelf: "flex-start", marginTop: 4 }} {...lock(has("documents.categories_manage"))}
                onClick={() => setEditingType({ category_id: cat.id, name: "", order_index: (typesByCategory[cat.id] || []).length })}>
                + Tip document
              </button>
            </div>
          </div>
        ))}
        {categories.length === 0 && <EmptyState text='Nicio categorie. Apasă „+ Categorie nouă".' />}
      </div>

      {editingCat && (
        <Modal title={editingCat.id ? "Editează categorie" : "Categorie nouă"} onClose={() => setEditingCat(null)}>
          <div style={{ display: "grid", gap: 12 }}>
            <div><label className="label label-req">Denumire</label>
              <input className="input" value={editingCat.name} onChange={e => setEditingCat({ ...editingCat, name: e.target.value })} data-testid="cat-name" /></div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setEditingCat(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={saveCat} data-testid="save-category">Salvează</button>
          </div>
        </Modal>
      )}

      {editingType && (
        <Modal title={editingType.id ? "Editează tip document" : "Tip document nou"} onClose={() => setEditingType(null)}>
          <div style={{ display: "grid", gap: 12 }}>
            <div><label className="label label-req">Denumire</label>
              <input className="input" value={editingType.name} onChange={e => setEditingType({ ...editingType, name: e.target.value })} data-testid="type-name" /></div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setEditingType(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={saveType} data-testid="save-type">Salvează</button>
          </div>
        </Modal>
      )}
    </>
  );
}
